"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import dynamic from "next/dynamic";
import { usePostHog } from "posthog-js/react";
import { showcaseEntries } from "@/app/service/showcase/showcaseData";
import HeroShowcaseSlider from "@/components/home/HeroShowcaseSlider";
import { FAQ_DATA } from "@/components/FAQSection/faq-data";
import V3WalkthroughVideo from "@/app/blog/_assets/components/V3WalkthroughVideo";
import HomeHeader from "@/components/home/HomeHeader";
import HeroSignupCard from "@/components/home/HeroSignupCard";

const ButtonSignin = dynamic(
  () => import("@/components/ButtonSignin"),
  { ssr: false }
);
const TestimonialSlider = dynamic(
  () => import("@/components/TestimonialSlider"),
  { ssr: false }
);
const FAQSection = dynamic(
  () => import("@/components/FAQSection/FAQStatic"),
  { ssr: false }
);
const LatestBlogPosts = dynamic(
  () => import("@/components/LatestBlogPosts"),
  { ssr: false }
);

export default function Home() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [user, setUser] = useState<any>(null);
  const posthog = usePostHog();

  useEffect(() => {
    import("@/libs/supabase/client").then(({ createClient }) => {
      const supabase = createClient();
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user) setUser(user);
      });
    });
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ_DATA.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <style jsx>{`
        @keyframes fadeInOut {
          0%, 40% { opacity: 1; }
          50%, 90% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes slideLeft {
          0%, 40% { transform: translateX(0); }
          50%, 90% { transform: translateX(-8px); }
          100% { transform: translateX(0); }
        }
        @keyframes slideRight {
          0%, 40% { transform: translateX(0); }
          50%, 90% { transform: translateX(8px); }
          100% { transform: translateX(0); }
        }
        @keyframes expandShrink {
          0%, 40% { width: 1rem; }
          50%, 90% { width: 3rem; }
          100% { width: 1rem; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); opacity: 0; }
          10% { opacity: 0.5; }
          90% { opacity: 0.5; }
          100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
        }
        @keyframes proofSlide {
          0%, 28%    { transform: translateY(0); }
          33%, 61%   { transform: translateY(-1.5rem); }
          66%, 94%   { transform: translateY(-3rem); }
          100%       { transform: translateY(-4.5rem); }
        }
        .hero-proof-slider {
          height: 1.5rem;
          overflow: hidden;
        }
        .hero-proof-track {
          display: flex;
          flex-direction: column;
          animation: proofSlide 9s infinite;
        }
        .hero-proof-track > span {
          height: 1.5rem;
          line-height: 1.5rem;
          white-space: nowrap;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-proof-track { animation: none; }
        }
      `}</style>
      <main className="min-h-screen bg-base-100">
      {/* Header */}
      <HomeHeader user={user} />

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Animated gradient mesh background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 lg:px-8 xl:px-12 py-8 sm:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center mb-12">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <div
                className="hero-proof-slider text-sm text-base-content/70 mb-4 text-center lg:text-left"
                aria-label="5.0 stars from 152+ reviews. 600+ songs delivered. 3 business day delivery."
              >
                <div className="hero-proof-track" aria-hidden="true">
                  <span>5.0 stars · 152+ reviews</span>
                  <span>600+ songs delivered</span>
                  <span>3 business day delivery</span>
                  <span>5.0 stars · 152+ reviews</span>
                </div>
              </div>
              <h1 className="text-3xl sm:text-5xl lg:text-4xl lg:tracking-tight font-extrabold mb-4 leading-tight">
                We Change Song Lyrics <span className="text-primary whitespace-nowrap">for You.</span>
              </h1>

              <p className="text-base sm:text-lg text-base-content/80 mb-4 font-medium">
                Send us the song and your new words. Our producers rebuild it with the original singer&apos;s voice and deliver in 3 business days. Starting at $50.
              </p>

              <div className="flex flex-col items-center lg:items-start gap-2 mt-2">
                <Link
                  href="/service"
                  className="btn btn-primary btn-lg"
                  onClick={() => posthog?.capture("home_hero_service_clicked")}
                >
                  Start Your Order
                </Link>
                <Link
                  href="/free-lyric-changer"
                  className="link link-hover text-base-content/70 text-sm"
                  onClick={() => posthog?.capture("home_hero_diy_clicked")}
                >
                  or DIY it: write your new lyrics free, then turn them into audio in Overdub Studio →
                </Link>
              </div>
            </div>

            {/* Audio Proof Card (replaces the hero video slot; video moves below the CTA cards) */}
            <div className="relative mt-6 lg:mt-0">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 blur-2xl lg:blur-3xl"></div>
              <div className="relative card bg-base-100 shadow-2xl border border-base-300">
                <div className="card-body">
                  <div className="badge badge-secondary">Hear real orders — before &amp; after</div>
                  <HeroShowcaseSlider />
                  <Link href="/service/showcase" className="link link-primary text-sm mt-1">
                    Hear {showcaseEntries.length - 1} more in the showcase →
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Two-Path CTA Cards — DFY first (service-first hero, 2026-10-17) */}
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* DFY Card */}
            <div className="card bg-base-100 shadow-xl border-2 border-secondary/20 hover:border-secondary/40 transition-colors">
              <div className="card-body">
                <div className="badge badge-secondary mb-2">Done For You</div>
                <h3 className="card-title text-xl">We'll change your lyrics, starting at $50</h3>
                <ul className="space-y-2 text-base-content/70 my-4">
                  <li className="flex items-start gap-2">
                    <span className="text-secondary mt-0.5">&#10003;</span>
                    <span><Link href="/service/showcase" className="link link-secondary">600+ songs delivered</Link>, <Link href="/service/reviews" className="link link-secondary">5-star reviews</Link></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary mt-0.5">&#10003;</span>
                    <span>3 business day delivery</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-secondary mt-0.5">&#10003;</span>
                    <span>No software or experience needed</span>
                  </li>
                </ul>
                <Link href="/service" className="btn btn-secondary w-full">
                  Order a Song
                </Link>
              </div>
            </div>

            {/* DIY Card */}
            <HeroSignupCard user={user} />
          </div>

          {/* Studio behind the songs (the Oct 2026 hero video, relocated from the hero slot) */}
          <div className="max-w-4xl mx-auto mt-14">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-6">The studio behind the songs</h2>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 blur-2xl"></div>
              <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl bg-black">
                <video
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                  poster="/general/changelyric_hero_oct_2026.webp"
                >
                  <source src="https://pub-6c4a08b7a6ba4982b5be067dec180888.r2.dev/brand-blog-images/changelyric/service/ChangeLyric_Hero_10.13.2026_v2.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Showcase */}
      <section id="features" className="relative py-16 sm:py-20 bg-base-100">
        {/* Subtle wave pattern */}
        <div className="absolute inset-0 opacity-50">
          <svg className="absolute bottom-0 w-full" viewBox="0 0 1440 320">
            <path fill="currentColor" fillOpacity="0.1" d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,112C672,96,768,96,864,112C960,128,1056,160,1152,160C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"></path>
          </svg>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              The Ultimate AI Lyric Changer Toolkit
            </h2>
            <p className="text-base-content/70 max-w-2xl mx-auto">
              Change lyrics of a song online, replace vocals, separate stems, all in one place
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto">
            {/* Change Lyrics */}
            <div className="flex flex-col items-center text-center group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative mb-6 p-8 bg-primary/10 rounded-2xl group-hover:bg-primary/20 transition-colors duration-500 w-64 h-48 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-primary-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                {/* Animated text change effect */}
                <div className="bg-base-100 p-3 rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-300 h-16 flex items-center justify-center">
                  <div className="text-sm font-mono text-center relative">
                    <div className="animate-pulse" style={{animation: 'fadeInOut 4s infinite'}}>
                      "Original text here"
                    </div>
                    <div className="absolute inset-0 opacity-0 text-primary font-medium animate-pulse" style={{animation: 'fadeInOut 4s infinite', animationDelay: '2s'}}>
                      "Your new text here"
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3">Change Lyrics</h3>
              <p className="text-base-content/70 leading-relaxed">
                Bulk-edit multiple sections instead of manual inpainting. Perfect for radio edits and licensed variations.
              </p>
            </div>

            {/* Change Singer */}
            <div className="flex flex-col items-center text-center group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative mb-6 p-8 bg-secondary/10 rounded-2xl group-hover:bg-secondary/20 transition-colors duration-500 w-64 h-48 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-secondary-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                {/* Voice transformation animation */}
                <div className="bg-base-100 p-3 rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-300 h-16 flex items-center justify-center">
                  <div className="flex justify-center items-center space-x-2">
                    <div className="w-2 h-8 bg-secondary rounded-full animate-pulse" style={{animationDelay: '0s'}}></div>
                    <div className="w-2 h-6 bg-secondary rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-10 bg-secondary rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                    <div className="w-2 h-4 bg-secondary rounded-full animate-pulse" style={{animationDelay: '0.6s'}}></div>
                    <div className="w-2 h-7 bg-secondary rounded-full animate-pulse" style={{animationDelay: '0.8s'}}></div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3">Change Singer</h3>
              <p className="text-base-content/70 leading-relaxed">
                200+ curated artist models, plus built-in custom voice training on all plans for the singer you can&apos;t find. Skip the $300+ session singer fees and get results fast.
              </p>
            </div>

            {/* Separate Vocals */}
            <div className="flex flex-col items-center text-center group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative mb-6 p-8 bg-accent/10 rounded-2xl group-hover:bg-accent/20 transition-colors duration-500 w-64 h-48 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-accent-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                {/* Separation animation */}
                <div className="bg-base-100 p-3 rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-300 h-16 flex items-center justify-center">
                  <div className="flex justify-center items-center space-x-1">
                    <div className="text-xs bg-accent/20 px-2 py-1 rounded shadow" style={{animation: 'slideLeft 3s infinite'}}>
                      Lead
                    </div>
                    <div className="w-4 h-0.5 bg-accent animate-pulse" style={{animation: 'expandShrink 3s infinite'}}></div>
                    <div className="text-xs bg-accent/20 px-2 py-1 rounded shadow" style={{animation: 'slideRight 3s infinite'}}>
                      Backing
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3">Separate Lead & Backing Vocals</h3>
              <p className="text-base-content/70 leading-relaxed">
                Stem separation for precise control. Edit leads and harmonies independently, no studio required.
              </p>
            </div>

            {/* Quick Rewrite Tools */}
            <div className="flex flex-col items-center text-center group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative mb-6 p-8 bg-info/10 rounded-2xl group-hover:bg-info/20 transition-colors duration-500 w-64 h-48 flex flex-col items-center justify-center">
                <div className="w-16 h-16 bg-info rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <svg className="w-8 h-8 text-info-content" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                {/* Progress steps animation */}
                <div className="bg-base-100 p-3 rounded-lg shadow-md group-hover:shadow-lg transition-shadow duration-300 h-16 flex items-center justify-center">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-info flex items-center justify-center animate-ping" style={{animationDelay: '0s', animationDuration: '3s'}}>
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                      </div>
                      <div className="text-xs mt-1 text-info animate-pulse" style={{animationDelay: '0s'}}>Upload</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-info flex items-center justify-center animate-ping" style={{animationDelay: '1s', animationDuration: '3s'}}>
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                      </div>
                      <div className="text-xs mt-1 text-info animate-pulse" style={{animationDelay: '1s'}}>Process</div>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="w-4 h-4 rounded-full bg-info flex items-center justify-center animate-ping" style={{animationDelay: '2s', animationDuration: '3s'}}>
                        <div className="w-1 h-1 rounded-full bg-white"></div>
                      </div>
                      <div className="text-xs mt-1 text-info animate-pulse" style={{animationDelay: '2s'}}>Done</div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="text-xl font-bold mb-3">Guided Section Workflow</h3>
              <p className="text-base-content/70 leading-relaxed">
                Overdub Studio generates each changed section on its own, with ten strategies to try when one misses. Mark your keepers, comp the rest.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Callout Banner */}
      <section className="py-12 sm:py-16 bg-secondary/10">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Not a producer? No problem.
            </h2>
            <p className="text-lg text-base-content/70 mb-6">
              Our team has delivered <Link href="/service/showcase" className="link link-secondary">600+ custom songs</Link> with <Link href="/service/reviews" className="link link-secondary">5-star reviews</Link>. Submit your lyrics, we handle the rest, starting at $50.
            </p>
            <Link href="/service" className="btn btn-secondary btn-lg">
              Learn About Our Service
            </Link>
          </div>
        </div>
      </section>

      {/* Overdub Studio Section */}
      <section className="py-16 sm:py-20 bg-base-200 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-16">
              <div className="badge badge-primary badge-lg mb-4">Overdub Studio</div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                Change Any Lyric. Shape the Sound You Want.
              </h2>
              <p className="text-lg text-base-content/70 max-w-2xl mx-auto">
                Drag in a track and tag the lyrics. Then generate each changed section with a strategy you pick. Generative AI does not land every take, so the Studio shows you each one and refunds the misses; trying another approach is a single click. No content filters. No limits.
              </p>
            </div>

            {/* V3 Walkthrough Video */}
            <V3WalkthroughVideo />

            {/* Hero Feature — One Guided Workspace */}
            <div className="relative rounded-2xl bg-gradient-to-br from-primary/10 via-base-100 to-secondary/10 border border-primary/20 p-8 sm:p-12 mb-12">
              <div className="absolute top-4 right-4 badge badge-primary badge-outline">Flagship</div>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-2xl sm:text-3xl font-bold mb-4">One Guided Workspace, Start to Finish</h3>
                  <p className="text-base-content/70 text-lg mb-6">
                    We tried whole-song generation in a single pass. When a section failed, there was no way to see why or try a different approach. Overdub Studio is the fix: it generates section by section and shows you every take, so you can swap strategies until one lands.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <div className="badge badge-lg gap-2 bg-base-100">
                      <span className="w-2 h-2 rounded-full bg-success"></span>
                      10 generation strategies
                    </div>
                    <div className="badge badge-lg gap-2 bg-base-100">
                      <span className="w-2 h-2 rounded-full bg-success"></span>
                      Keep/bad take comping
                    </div>
                    <div className="badge badge-lg gap-2 bg-base-100">
                      <span className="w-2 h-2 rounded-full bg-success"></span>
                      Auto-refunded failures
                    </div>
                  </div>
                </div>
                <div className="flex justify-center">
                  <div className="relative w-64 h-48">
                    {/* Stylized song structure visualization */}
                    <div className="absolute inset-0 flex flex-col gap-2 justify-center">
                      {["Verse 1", "Chorus", "Verse 2", "Bridge", "Outro"].map((section, i) => (
                        <div key={section} className="flex items-center gap-3">
                          <div
                            className="h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center px-3 transition-all"
                            style={{ width: `${[85, 100, 85, 60, 70][i]}%`, animationDelay: `${i * 0.1}s` }}
                          >
                            <span className="text-xs font-mono text-primary truncate">{section}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Bento Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {/* AI Engine — spans 2 cols on lg */}
              <div className="lg:col-span-2 rounded-2xl bg-base-100 border border-base-300 p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Presenting: The Overdub Engine</h3>
                  <p className="text-base-content/70">
                    Strong vocal output with tight timing and solid tone matching. Not every take lands, and the Studio plans for that. You see each miss and get the credits back; running another strategy on the next take is the whole loop.
                  </p>
                </div>
              </div>

              {/* Strategy count — stat card */}
              <div className="rounded-2xl bg-base-100 border border-base-300 p-6 sm:p-8 flex flex-col justify-between">
                <div className="w-14 h-14 rounded-xl bg-warning/10 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-warning" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <div className="text-4xl font-extrabold text-primary mb-1">10</div>
                  <p className="text-sm text-base-content/70">generation strategies to try on any section, from auto extension to inpaint</p>
                </div>
              </div>

              {/* Stems */}
              <div className="rounded-2xl bg-base-100 border border-base-300 p-6 sm:p-8">
                <div className="w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Stems, Not a Baked Bounce</h3>
                <p className="text-base-content/70 text-sm">
                  Download the original stems ZIP plus comp lanes that place your kept takes on the song timeline. DAW-ready.
                </p>
              </div>

              {/* Zero Content Filters */}
              <div className="rounded-2xl bg-base-100 border border-base-300 p-6 sm:p-8">
                <div className="w-14 h-14 rounded-xl bg-error/10 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Zero Content Filters</h3>
                <p className="text-base-content/70 text-sm">
                  No censorship, no copyright flags, no word limits. Write whatever you need.
                </p>
              </div>

              {/* Streamlined Workflow */}
              <div className="rounded-2xl bg-base-100 border border-base-300 p-6 sm:p-8">
                <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center mb-4">
                  <svg className="w-7 h-7 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">Built for Iteration</h3>
                <p className="text-base-content/70 text-sm">
                  Transcription, section timing, and vocal isolation run while you write lyrics. Takes land per section and keepers drop onto a timeline.
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              {user ? (
                <Link href="/dashboard" className="btn btn-primary btn-lg">
                  Open the Dashboard
                </Link>
              ) : (
                <ButtonSignin text="Try ChangeLyric Free" extraStyle="btn-lg btn-primary" />
              )}
              <p className="text-sm text-base-content/60 mt-3">
                Free trial included, cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 sm:py-20 bg-base-200">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            Lyric Swaps, Simplified
          </h2>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* Step 1 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold text-xl">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Drop In Any Audio File</h3>
                  <p className="text-base-content/70">
                    MP3, WAV, M4A, FLAC, or OGG up to 100MB. The file converts, transcribes, and isolates vocals while you keep working.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold text-xl">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Tag the Lyrics and Lock Sections</h3>
                  <p className="text-base-content/70">
                    Search the built-in lyrics database or paste your own with [section tags]. Lock the sections and the tool maps each one onto the transcript.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold text-xl">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Generate and Mark Your Keepers</h3>
                  <p className="text-base-content/70">
                    Queue a take per changed section, starting with auto extension. Listen and checkmark what works. When a take misses, the tool refunds the credits and you simply try another strategy.
                  </p>
                </div>
              </div>

              {/* Step 4 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold text-xl">
                    4
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Export Stems and Comp Lanes</h3>
                  <p className="text-base-content/70">
                    Download the original stems ZIP plus comp lanes that place your kept takes on the song timeline, isolated to the vocal.
                  </p>
                </div>
              </div>

              {/* Step 5 */}
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-primary text-primary-content rounded-full flex items-center justify-center font-bold text-xl">
                    5
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Mix It Yourself</h3>
                  <p className="text-base-content/70">
                    Import stems into any DAW. Full control over levels, EQ, and placement. The final mix is yours to shape.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Demo Section - Moved to hero */}

      {/* Testimonials */}
      <section id="testimonials" className="py-16 sm:py-20 bg-base-100">
        <div className="container mx-auto px-4 text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Loved by 600+ Creators</h2>
          <p className="text-base-content/70 mb-6 max-w-2xl mx-auto">
            Rated 5.0 on Google. Read our <Link href="/service/reviews#google-reviews" className="link link-secondary">verified Google reviews</Link>, or confirm them straight on Google.
          </p>
          <a
            href="https://www.google.com/maps/place/ChangeLyric/@46.423669,-129.9427085,3z/data=!3m1!4b1!4m6!3m5!1s0x2021adc2183cb309:0x30efe1242129cd25!8m2!3d46.423669!4d-129.9427086!16s%2Fg%2F11nnvcz758?hl=en-US&entry=ttu&g_ep=EgoyMDI2MDcyNi4wIKXMDSoASAFQAw%3D%3D"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary btn-outline gap-2"
          >
            <svg className="w-5 h-5 text-warning" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M10.868 2.884c-.321-.772-1.415-.772-1.736 0l-1.83 4.401-4.753.381c-.833.067-1.171 1.107-.536 1.651l3.62 3.102-1.106 4.637c-.194.813.691 1.456 1.405 1.02L10 15.591l4.069 2.485c.713.436 1.598-.207 1.404-1.02l-1.106-4.637 3.62-3.102c.635-.544.297-1.584-.536-1.65l-4.752-.382-1.831-4.401z" />
            </svg>
            5.0 on Google. Verify Reviews
          </a>
        </div>
        <TestimonialSlider />
      </section>

      {/* About Section */}
      <section id="about" className="relative py-16 sm:py-20 bg-gradient-to-br from-base-200 via-base-200/95 to-base-300/50">
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.02'/%3E%3C/svg%3E")`
        }}></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            About the Creator
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Profile Image */}
              <div className="flex-shrink-0">
                <div className="w-48 h-48 rounded-full overflow-hidden shadow-xl">
                  <Image 
                    src="/general/willmason.jpg" 
                    alt="Will Mason"
                    width={192}
                    height={192}
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>
              
              {/* Bio Content */}
              <div className="flex-grow text-center md:text-left">
                <h3 className="text-2xl font-bold mb-4">Will Mason</h3>
                               <p className="text-base-content/70 mb-4">
                  Hey, I'm Will. Started playing guitar at 15, now I build digital music tools!
                </p>
                <p className="text-base-content/70 mb-4">
                  For two years, our done-for-you service handled custom lyric swaps – charging hundreds per song,
                  taking days to deliver. Clients love the results, but I knew there had to be a better way.
                </p>
                <p className="text-base-content/70 mb-4">
                  Some people wanted a cheaper way to do it themselves: that's ChangeLyric. Others still want the full-service experience with a human touch, <Link href="/service" className="link link-primary">we still do that too</Link>.
                </p>  

                 <p className="text-base-content/70 mb-4">
                  
                  No content filters either – create whatever you want. (Just remember: you're responsible for how you use it.)
                </p>

                <p className="text-base-content/70 mb-4">
                  ChangeLyric is basically my workflow in webapp form. Similar to the process I used to follow, 
                  minus the wait and the invoice for my human time.
                </p>

                <p className="text-base-content/70">
                  Still improving it daily. With your feedback, I will make sure your lyric modification needs are covered!
                  Let's make something amazing together.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Free Lyric Changer Callout */}
      <section className="relative py-12 sm:py-16 bg-gradient-to-br from-primary/5 via-base-100 to-secondary/5 border-y border-base-content/5">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              100% Free. No subscription.
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Not ready to commit? Rewrite the words first, free.
            </h2>
            <p className="text-lg text-base-content/70 mb-6">
              Use the <Link href="/free-lyric-changer" className="link link-accent">free AI lyric changer</Link> to rewrite any song&apos;s words with AI. Search the song, describe the new version, get matched-rhythm lyrics in seconds. No payment required, upgrade to audio whenever you&apos;re ready.
            </p>
            <Link href="/free-lyric-changer" className="btn btn-accent btn-lg">
              Try the free lyric changer
            </Link>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section id="faq" className="relative py-16 sm:py-20 overflow-hidden">
        {/* Animated shapes */}
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary/5 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute -bottom-10 -left-10 w-60 h-60 bg-secondary/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '3s'}}></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-8">
              Frequently Asked Questions
            </h2>
            
            <FAQSection />
          </div>
        </div>
      </section>

      {/* Latest Blog Posts */}
      <LatestBlogPosts />

      {/* Final CTA */}
      <section className="relative py-16 sm:py-20 overflow-hidden bg-base-200">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 bg-[length:200%_100%] animate-shimmer"></div>
        {/* Radial glow */}
        <div className="absolute inset-0 bg-radial-gradient from-white/5 to-transparent"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            ChangeLyric Is One Click Away
          </h2>
          <p className="text-xl text-base-content/70 mb-8">
            Professional lyric modification: do it yourself or let us handle it.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!user ? (
              <ButtonSignin text="Start Free Trial" extraStyle="btn-primary btn-lg" />
            ) : (
              <Link href="/dashboard" className="btn btn-primary btn-lg">
                Go to Dashboard
              </Link>
            )}
            <Link href="/service" className="btn btn-outline btn-secondary btn-lg">
              Order Done-For-You
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-base-200 py-8 border-t border-base-300">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
              <Link href="/service" className="link link-hover">Service</Link>
              <Link href="/contact" className="link link-hover">Contact</Link>
              <Link href="/privacy-policy" className="link link-hover">Privacy</Link>
              <Link href="/tos" className="link link-hover">Terms</Link>
              <a href="mailto:contact@changelyric.com" className="link link-hover">Support</a>
              <Link href="/producer/apply" className="link link-hover">Become a Producer</Link>
              {user && (
                <button
                  onClick={async () => {
                    const { createClient } = await import("@/libs/supabase/client");
                    const supabase = createClient();
                    await supabase.auth.signOut();
                    window.location.href = '/';
                  }}
                  className="link link-hover"
                >
                  Logout
                </button>
              )}
            </div>
            
            <p className="text-sm text-base-content/60" suppressHydrationWarning>
              © {new Date().getFullYear()} ChangeLyric
            </p>
          </div>
        </div>
      </footer>
    </main>
    </>
  );
}